import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Address } from 'viem';
import { useConfig } from 'wagmi';
import {
  initiateWithdraw,
  initiateWithdrawSignature,
} from '../components/PortfolioOverviewPage/initiateWithdraw';
import { isUserRejectedError } from '../lib/errors';
import { invalidateQueries } from '../lib/reactQueryHelpers';
import { showToast } from '../lib/showToast';
import { withdrawFromSubAccountFailedToastMessage } from '../lib/toasts';
import { assertRequiredData } from '../lib/typeGuards';
import { useDataForSigning } from './useDataForSigning';
import { getPendingWithdrawQuery } from './usePendingWithdraw';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';
import { subaccountBalanceQuery } from './useSubaccountBalance';

export type InitiateWithdrawSignatureMutateParams = {
  tokenId: string;
  token: Address;
  amount: bigint;
};

export const useInitiateWithdrawSignature = () => {
  const { domain, signedTypedDataTypes } = useDataForSigning();
  const config = useConfig();
  const selectedSubaccount = useActiveSelectedSubaccount();
  return useMutation({
    mutationKey: ['initiateWithdrawSignature', selectedSubaccount?.id],
    mutationFn: async ({ tokenId, token, amount }: InitiateWithdrawSignatureMutateParams) => {
      const params = {
        subaccountName: selectedSubaccount?.name,
        account: selectedSubaccount?.account,
        config,
        domain,

        signedTypedDataTypes,
      };
      assertRequiredData(params);
      return initiateWithdrawSignature({
        ...params,
        partialInitiateWithdrawData: { tokenId, token, amount },
      });
    },
  });
};

export const getInitiateWithdrawKey = (subaccountId: string | undefined) => [
  'initiateWithdraw',
  subaccountId,
];
export const useInitiateWithdraw = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const selectedSubaccount = useActiveSelectedSubaccount();
  return useMutation({
    mutationKey: getInitiateWithdrawKey(selectedSubaccount?.id),
    mutationFn: initiateWithdraw,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getPendingWithdrawQuery(selectedSubaccount?.id).queryKey,
        refetchType: 'all',
      });
      invalidateQueries(queryClient, subaccountBalanceQuery(selectedSubaccount?.id).queryKey);

      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (err) => {
      if (isUserRejectedError(err)) {
        return;
      }
      console.error('Error withdrawing', err);
      showToast(withdrawFromSubAccountFailedToastMessage(selectedSubaccount?.name));
    },
  });
};
